import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../features/auth/authSlice';
import Card, { CardHeader } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { MobileCard, DataRow } from '../../components/ui/MobileCard';
import { SkeletonTable, SkeletonFields } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import UuidActionForm from '../../components/admin/UuidActionForm';
import { authErrorMessage } from '../../lib/authErrorMessage';
import { createAdminSchema, updateAdminSchema } from '../../lib/validators/adminSchemas';
import {
  useListAdminsQuery,
  useGetAdminQuery,
  useCreateAdminMutation,
  useUpdateAdminMutation,
  useDeleteAdminMutation,
  useRevokeAdminMutation,
  useMakeAdminMutation,
  useUnlockUserMutation,
} from '../../features/admin/adminAccountsApi';

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function applyFieldErrors(err, setError) {
  const apiErrors = err?.data?.errors;
  if (apiErrors) {
    Object.entries(apiErrors).forEach(([field, messages]) => setError(field, { message: messages[0] }));
    return true;
  }
  return false;
}

function CreateAdminForm({ onDone }) {
  const toast = useToast();
  const [createAdmin, { isLoading }] = useCreateAdminMutation();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ resolver: zodResolver(createAdminSchema) });

  const onSubmit = async (values) => {
    try {
      await createAdmin(values).unwrap();
      toast.push({ tone: 'success', title: 'Administrator created', message: `Login credentials were emailed to ${values.email}.` });
      onDone?.();
    } catch (err) {
      if (!applyFieldErrors(err, setError)) {
        setError('root', { message: authErrorMessage(err, { fallback: 'Could not create the admin account.' }) });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div className="grid grid-cols-2 gap-3">
        <Input label="First name" error={errors.first_name?.message} {...register('first_name')} />
        <Input label="Last name" error={errors.last_name?.message} {...register('last_name')} />
      </div>
      <Input label="Email address" type="email" error={errors.email?.message} {...register('email')} />
      <Input label="Phone number" placeholder="+2348012345678" error={errors.phone?.message} {...register('phone')} />
      <p className="text-xs text-foreground-subtle -mt-1">
        A password is generated automatically and emailed to this address, along with a forced password change on first login.
      </p>
      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      <Button type="submit" className="w-full" isLoading={isLoading}>
        Create account
      </Button>
    </form>
  );
}

function EditAdminForm({ admin, onDone }) {
  const toast = useToast();
  const [updateAdmin, { isLoading }] = useUpdateAdminMutation();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(updateAdminSchema),
    defaultValues: { first_name: admin.first_name, last_name: admin.last_name, phone: admin.phone || '' },
  });

  const onSubmit = async (values) => {
    try {
      await updateAdmin({ uuid: admin.id, ...values }).unwrap();
      toast.push({ tone: 'success', message: 'Administrator profile updated.' });
      onDone?.();
    } catch (err) {
      if (!applyFieldErrors(err, setError)) {
        setError('root', { message: authErrorMessage(err, { fallback: 'Could not update this account.' }) });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div className="grid grid-cols-2 gap-3">
        <Input label="First name" error={errors.first_name?.message} {...register('first_name')} />
        <Input label="Last name" error={errors.last_name?.message} {...register('last_name')} />
      </div>
      <Input label="Phone number" placeholder="+2348012345678" error={errors.phone?.message} {...register('phone')} />
      <p className="text-xs text-foreground-subtle -mt-1">Email, password, and role can't be changed from here.</p>
      {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      <Button type="submit" className="w-full" isLoading={isLoading}>
        Save changes
      </Button>
    </form>
  );
}

const PAGE_SIZE = 8;

const ROLE_TONE = { 'super-admin': 'success', admin: 'neutral', investor: 'warning' };

/**
 * Live accounts can hold more than one role at once (e.g. an investor who
 * was later granted admin keeps both) — the real /admin/admins response
 * confirmed this (`role: ["investor", "admin"]`), which the docs' "one role
 * from a fixed set" framing doesn't quite capture. Show every role, not
 * just the first, so a dual-role account doesn't look like a plain admin.
 */
function RoleBadges({ roles }) {
  const list = roles?.length ? roles : ['admin'];
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((role) => (
        <Badge key={role} tone={ROLE_TONE[role] || 'neutral'}>
          {role.toUpperCase()}
        </Badge>
      ))}
    </div>
  );
}

/**
 * GET /admin/admins/{uuid} detail view. Fetched fresh (not just reused from
 * the list row) since the spec calls this out as its own endpoint — also
 * the only place created_at/updated_at are shown. The 404 case ("doesn't
 * exist, belongs to an investor, or belongs to a super-admin" — all
 * indistinguishable per the docs) renders as one generic message rather
 * than trying to guess which.
 */
function AdminDetailModal({ uuid, onClose, onEdit, onRevoke, onDelete, isSelf }) {
  const { data: admin, isLoading, isError, error } = useGetAdminQuery(uuid, { skip: !uuid });

  return (
    <Modal open={!!uuid} onClose={onClose} title="Administrator details">
      {isLoading && <SkeletonFields fields={4} />}

      {isError && (
        <p className="py-6 text-center text-danger">
          {authErrorMessage(error, { notFound: 'Administrator not found.' })}
        </p>
      )}

      {admin && (
        <div className="space-y-4">
          <div>
            <p className="text-base font-semibold text-foreground">
              {admin.full_name}
              {isSelf && <span className="ml-2 text-xs font-normal text-foreground-subtle">(you)</span>}
            </p>
            <p className="text-sm text-foreground-muted">{admin.email}</p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <DataRow label="Phone" value={admin.phone || '—'} />
            <DataRow label="Must change password" value={admin.must_change_password ? 'Yes' : 'No'} />
            <DataRow label="Created" value={formatDate(admin.created_at)} />
            <DataRow label="Last updated" value={formatDate(admin.updated_at)} />
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-muted mb-1.5">Roles</p>
            <RoleBadges roles={admin.roles} />
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
            <Button variant="ghost" size="sm" onClick={() => onEdit(admin)}>
              Edit
            </Button>
            <Button variant="ghost" size="sm" disabled={isSelf} onClick={() => onRevoke(admin)}>
              Revoke
            </Button>
            <Button variant="danger" size="sm" disabled={isSelf} onClick={() => onDelete(admin)}>
              Delete
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function AdminAccounts() {
  const currentUser = useSelector(selectCurrentUser);
  const { data, isLoading, isFetching } = useListAdminsQuery();
  const [deleteAdmin, { isLoading: isDeleting }] = useDeleteAdminMutation();
  const [revokeAdmin, { isLoading: isRevoking }] = useRevokeAdminMutation();
  const [makeAdmin, makeAdminState] = useMakeAdminMutation();
  const [unlockUser, unlockState] = useUnlockUserMutation();
  const toast = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailUuid, setDetailUuid] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // { admin, action: 'delete' | 'revoke' }
  const [page, setPage] = useState(1);

  const runUnlock = async (admin) => {
    try {
      await unlockUser(admin.id).unwrap();
      toast.push({ tone: 'success', message: `${admin.full_name}'s account was unlocked.` });
    } catch (err) {
      toast.push({
        tone: 'danger',
        title: 'Unlock failed',
        message: authErrorMessage(err, { notFound: 'No account exists with that UUID.' }),
      });
    }
  };

  const admins = data ?? [];
  const totalPages = Math.max(1, Math.ceil(admins.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedAdmins = admins.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const closeConfirm = () => setConfirmTarget(null);

  const runConfirmed = async () => {
    if (!confirmTarget) return;
    const { admin, action } = confirmTarget;
    try {
      if (action === 'delete') {
        await deleteAdmin(admin.id).unwrap();
        toast.push({ tone: 'success', message: `${admin.full_name} was removed.` });
      } else {
        await revokeAdmin(admin.id).unwrap();
        toast.push({ tone: 'success', message: `Admin privileges revoked for ${admin.full_name}.` });
      }
      closeConfirm();
    } catch (err) {
      toast.push({
        tone: 'danger',
        title: action === 'delete' ? 'Delete failed' : 'Revoke failed',
        message: authErrorMessage(err, { notFound: 'This account no longer holds the admin role.' }),
      });
      closeConfirm();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Manage administrators</h1>
          <p className="text-foreground-muted text-sm mt-1">Super Admin only — create, edit, and revoke admin access.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="self-start sm:self-auto">
          Add administrator
        </Button>
      </div>

      <Card>
        <CardHeader title="Administrators" subtitle={isFetching && !isLoading ? 'Refreshing…' : undefined} />
        <p className="text-xs text-foreground-subtle -mt-3 mb-4">
          Revoke keeps the account and signs them out of admin only — they can still log in as a regular user. Delete permanently
          removes the account and ends every session.
        </p>

        {isLoading && <SkeletonTable columns={['Name', 'Email', 'Phone', 'Role', 'Status', 'Actions']} />}
        {!isLoading && admins.length === 0 && <p className="py-8 text-center text-foreground-muted">No admin accounts yet.</p>}

        {!isLoading && admins.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-foreground-subtle border-b border-border">
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Phone</th>
                    <th className="py-2 pr-4 font-medium">Role</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pagedAdmins.map((admin) => {
                    const isSelf = currentUser?.id && admin.id === currentUser.id;
                    return (
                      <tr key={admin.id}>
                        <td className="py-3 pr-4 text-foreground">
                          {admin.full_name}
                          {isSelf && <span className="ml-2 text-xs text-foreground-subtle">(you)</span>}
                        </td>
                        <td className="py-3 pr-4 text-foreground-muted">{admin.email}</td>
                        <td className="py-3 pr-4 text-foreground-muted">{admin.phone || '—'}</td>
                        <td className="py-3 pr-4">
                          <RoleBadges roles={admin.roles} />
                        </td>
                        <td className="py-3 pr-4">
                          {admin.must_change_password && <Badge tone="warning">Must change password</Badge>}
                        </td>
                        <td className="py-3">
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setDetailUuid(admin.id)}>
                              View
                            </Button>
                            <Button variant="ghost" size="sm" isLoading={unlockState.isLoading} onClick={() => runUnlock(admin)}>
                              Unlock
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditTarget(admin)}>
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={isSelf}
                              title={isSelf ? "You can't revoke your own admin access." : undefined}
                              onClick={() => setConfirmTarget({ admin, action: 'revoke' })}
                            >
                              Revoke
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={isSelf}
                              title={isSelf ? "You can't delete your own account." : undefined}
                              onClick={() => setConfirmTarget({ admin, action: 'delete' })}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {pagedAdmins.map((admin) => {
                const isSelf = currentUser?.id && admin.id === currentUser.id;
                return (
                  <MobileCard key={admin.id}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-medium text-foreground">
                        {admin.full_name}
                        {isSelf && <span className="ml-2 text-xs text-foreground-subtle">(you)</span>}
                      </p>
                      <RoleBadges roles={admin.roles} />
                    </div>
                    <DataRow label="Email" value={admin.email} />
                    <DataRow label="Phone" value={admin.phone || '—'} />
                    {admin.must_change_password && (
                      <div className="pt-1">
                        <Badge tone="warning">Must change password</Badge>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button variant="ghost" size="sm" className="flex-1" onClick={() => setDetailUuid(admin.id)}>
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1"
                        isLoading={unlockState.isLoading}
                        onClick={() => runUnlock(admin)}
                      >
                        Unlock
                      </Button>
                      <Button variant="ghost" size="sm" className="flex-1" onClick={() => setEditTarget(admin)}>
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="flex-1"
                        disabled={isSelf}
                        onClick={() => setConfirmTarget({ admin, action: 'revoke' })}
                      >
                        Revoke
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        className="flex-1"
                        disabled={isSelf}
                        onClick={() => setConfirmTarget({ admin, action: 'delete' })}
                      >
                        Delete
                      </Button>
                    </div>
                  </MobileCard>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-border">
                <p className="text-xs text-foreground-subtle">
                  Page {safePage} of {totalPages} · {admins.length} total
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={safePage <= 1}
                    onClick={() => setPage(Math.max(1, safePage - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      <div className="grid sm:grid-cols-2 gap-6">
        <Card>
          <CardHeader title="Grant admin access" subtitle="Promote an existing account to admin." />
          <UuidActionForm
            actionLabel="Grant admin access"
            helperText="Search by name, email, or UUID covers existing admin accounts for now. There's no directory yet for investor accounts — use the manual UUID option for those. Every existing session for the account is signed out immediately after granting access."
            onRun={makeAdmin}
            isLoading={makeAdminState.isLoading}
            successMessage="Admin access granted."
            notFoundMessage="That account either doesn't exist or already has admin/super-admin access."
            confirmTitle="Grant admin access?"
            confirmMessage="This immediately signs the account out of every existing session. They'll need to log in again — and complete 2FA — before they can use admin capabilities."
            lookup="admins"
          />
        </Card>

        <Card>
          <CardHeader title="Unlock a locked-out account" subtitle="Clears repeated-failed-login lockout for any account." />
          <UuidActionForm
            actionLabel="Unlock account"
            helperText="Search finds admin accounts by name, email, or UUID. Investor accounts aren't searchable yet — use the manual UUID option for those."
            onRun={unlockUser}
            isLoading={unlockState.isLoading}
            successMessage="Account unlocked."
            notFoundMessage="No account exists with that UUID."
            lookup="admins"
          />
        </Card>
      </div>

      <AdminDetailModal
        uuid={detailUuid}
        isSelf={!!currentUser?.id && detailUuid === currentUser.id}
        onClose={() => setDetailUuid(null)}
        onEdit={(admin) => {
          setDetailUuid(null);
          setEditTarget(admin);
        }}
        onRevoke={(admin) => {
          setDetailUuid(null);
          setConfirmTarget({ admin, action: 'revoke' });
        }}
        onDelete={(admin) => {
          setDetailUuid(null);
          setConfirmTarget({ admin, action: 'delete' });
        }}
      />

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Add administrator">
        <CreateAdminForm onDone={() => setCreateOpen(false)} />
      </Modal>

      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title="Edit administrator">
        {editTarget && <EditAdminForm key={editTarget.id} admin={editTarget} onDone={() => setEditTarget(null)} />}
      </Modal>

      <ConfirmDialog
        open={!!confirmTarget}
        onClose={closeConfirm}
        onConfirm={runConfirmed}
        isLoading={confirmTarget?.action === 'delete' ? isDeleting : isRevoking}
        title={confirmTarget?.action === 'delete' ? 'Delete administrator' : 'Revoke admin privileges'}
        confirmLabel={confirmTarget?.action === 'delete' ? 'Delete' : 'Revoke'}
        message={
          confirmTarget?.action === 'delete'
            ? `This permanently deletes ${confirmTarget?.admin?.full_name}'s account and revokes every session. This can't be undone.`
            : `${confirmTarget?.admin?.full_name} keeps their account and can still log in, but loses admin access and the 2FA requirement immediately.`
        }
      />
    </div>
  );
}
